import React, { useCallback, useContext, useMemo, useState } from 'react';
import { WorkspaceContext } from '../../contexts/WorkspaceContext';
import { useUsers } from '../../hooks/useUsers';
import authService from '../../auth/authService';
import Modal from '../shared/Modal';
import './NewDMModal.css';

const AVATAR_COLORS = [
  '#4d94f8', '#34d399', '#f472b6', '#fbbf24', '#a78bfa',
  '#fb923c', '#38bdf8', '#f87171', '#818cf8', '#2dd4bf',
];

function getAvatarColor(name) {
  const code = (name || 'A').charCodeAt(0);
  return AVATAR_COLORS[code % AVATAR_COLORS.length];
}

function NewDMModal({ onClose }) {
  const { createDM, createGroupDM } = useContext(WorkspaceContext);
  const { users, loading: usersLoading } = useUsers();
  const [query, setQuery] = useState('');
  const [selectedUserIds, setSelectedUserIds] = useState([]);
  const [error, setError] = useState('');
  const [isSubmitting, setIsSubmitting] = useState(false);
  const signedInUsername = (authService.getEmail() || '').toLowerCase();

  const filteredUsers = useMemo(() => {
    const nonAgents = users.filter(
      (u) => !u.isAgent && (u.username || '').toLowerCase() !== signedInUsername
    );
    if (!query.trim()) return nonAgents;
    const q = query.toLowerCase();
    return nonAgents.filter(
      (u) =>
        u.displayName.toLowerCase().includes(q) ||
        u.username.toLowerCase().includes(q)
    );
  }, [query, users, signedInUsername]);

  const selectedCount = selectedUserIds.length;

  const toggleSelectedUser = useCallback((username) => {
    setSelectedUserIds((previous) => (
      previous.includes(username)
        ? previous.filter((id) => id !== username)
        : [...previous, username]
    ));
    setError('');
  }, []);

  const handleSubmit = useCallback(async (event) => {
    event.preventDefault();
    if (isSubmitting || selectedUserIds.length === 0) return;

    setIsSubmitting(true);
    setError('');
    try {
      if (selectedUserIds.length === 1) {
        await createDM(selectedUserIds[0]);
      } else {
        await createGroupDM(selectedUserIds);
      }
      onClose();
    } catch {
      setError('Failed to start conversation. Please try again.');
    } finally {
      setIsSubmitting(false);
    }
  }, [createDM, createGroupDM, isSubmitting, onClose, selectedUserIds]);

  const submitLabel = selectedCount <= 1
    ? 'Start direct message'
    : `Create group DM (${selectedCount})`;

  return (
    <Modal title="New Message" onClose={onClose}>
      <form className="ndm-container" onSubmit={handleSubmit}>
        {/* Search */}
        <div className="ndm-search-wrap">
          <svg className="ndm-search-icon" viewBox="0 0 20 20" fill="currentColor" width="16" height="16">
            <path fillRule="evenodd" d="M9 3.5a5.5 5.5 0 100 11 5.5 5.5 0 000-11zM2 9a7 7 0 1112.452 4.391l3.328 3.329a.75.75 0 11-1.06 1.06l-3.329-3.328A7 7 0 012 9z" clipRule="evenodd" />
          </svg>
          <input
            className="ndm-search-input"
            type="text"
            value={query}
            onChange={(e) => setQuery(e.target.value)}
            placeholder="Search by name or email..."
            autoFocus
          />
        </div>

        {/* Error */}
        {error && <p className="ndm-error">{error}</p>}

        {/* User list */}
        <div className="ndm-user-list">
          {usersLoading ? (
            <div className="ndm-empty">Loading users...</div>
          ) : filteredUsers.length === 0 ? (
            <div className="ndm-empty">No users found</div>
          ) : (
            filteredUsers.map((user) => {
              const initial = (user.displayName || user.username).charAt(0).toUpperCase();
              const isSelected = selectedUserIds.includes(user.username);
              return (
                <button
                  key={user.username}
                  type="button"
                  className={`ndm-user-item ${isSelected ? 'is-selected' : ''}`}
                  onClick={() => toggleSelectedUser(user.username)}
                  aria-pressed={isSelected}
                >
                  <span
                    className="ndm-avatar"
                    style={{ backgroundColor: getAvatarColor(user.displayName || user.username) }}
                  >
                    {initial}
                  </span>
                  <div className="ndm-user-info">
                    <span className="ndm-user-name">{user.displayName}</span>
                    <span className="ndm-user-email">{user.username}</span>
                  </div>
                  <span className={`ndm-checkbox ${isSelected ? 'is-selected' : ''}`} aria-hidden="true">
                    {isSelected ? '✓' : ''}
                  </span>
                </button>
              );
            })
          )}
        </div>

        <div className="ndm-footer">
          <span className="ndm-selected-summary">
            {selectedCount === 0 && 'Select at least one person'}
            {selectedCount === 1 && '1 person selected'}
            {selectedCount > 1 && `${selectedCount} people selected`}
          </span>
          <button
            type="submit"
            className="ndm-submit"
            disabled={selectedCount === 0 || isSubmitting}
          >
            {submitLabel}
          </button>
        </div>
      </form>
    </Modal>
  );
}

export default NewDMModal;
